/**
 * pagination — envelope abstractions for `useApiInfiniteQuery` /
 * `useApiCursorQuery` (A5-440).
 *
 * Tyndo gateway endpoints do NOT share one list envelope. Three shapes exist:
 *
 *   1. PaginatedResponse  — `{ data, total, page, pageSize, hasMore }`
 *                           (the original, most modules' CRUD list endpoints).
 *   2. OffsetEnvelope     — `{ items, offset, limit, totalCount }`
 *                           (historian / audit range queries).
 *   3. CursorEnvelope     — `{ results, cursor }` (event-stream / large
 *                           genealogy walks where an offset is meaningless).
 *
 * `useApiInfiniteQuery` was hard-wired to shape (1): it assumed `hasMore` +
 * `page` and broke on the other two. This module gives each shape a small
 * adapter — `getItems` (flatten pages) + `getNextPageParam` (advance) — so the
 * infinite-query hook can be parameterised by envelope instead of forking.
 *
 * Which endpoint uses which envelope is documented per family in
 * docs (see the hooks README); when in doubt, inspect the response: a `cursor`
 * field ⇒ cursor; an `offset`/`limit` ⇒ offset; otherwise page-based.
 */

/** Page-based envelope (the original PaginatedResponse). */
export interface PageEnvelope<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/** Offset/limit envelope (historian + audit range queries). */
export interface OffsetEnvelope<T> {
  items: T[];
  offset: number;
  limit: number;
  totalCount: number;
}

/** Cursor envelope (event streams / unbounded genealogy walks). */
export interface CursorEnvelope<T> {
  results: T[];
  /** Opaque continuation token; null/absent ⇒ no further pages. */
  cursor?: string | null;
}

/**
 * Canonical server list envelope (`Tyndo.Core.Http.ListEnvelope<T>`) —
 * `{ items, totalCount, skip, top }`, produced by endpoints that parse
 * `$top`/`$skip` through the shared `ListPaging` clamp (C-1). New list
 * endpoints converge on this shape; prefer it over the legacy three above
 * when consuming a converted endpoint (EBR review queue, alarm suppressions,
 * MES handshakes, ...).
 */
export interface ListEnvelope<T> {
  items: T[];
  /** Pre-paging total across the whole filtered set. */
  totalCount: number;
  /** Resolved window the server actually applied (post-clamp). */
  skip: number;
  top: number;
}

/**
 * Adapter describing how to read items and compute the next page param from a
 * given envelope shape. `PageParam` is the type of the value appended to the
 * request (a 1-based page number, a numeric offset, or an opaque cursor).
 */
export interface PaginationAdapter<TEnvelope, TItem, PageParam> {
  /** Initial page param for the first request. */
  initialPageParam: PageParam;
  /** Flatten a page into its item array. */
  getItems: (page: TEnvelope) => TItem[];
  /**
   * Compute the next page param given the last page and its param, or
   * `undefined` when there are no further pages.
   */
  getNextPageParam: (lastPage: TEnvelope, lastPageParam: PageParam) => PageParam | undefined;
  /**
   * Build the query-string fragment that requests the given page param,
   * appended after the endpoint's existing query (the hook supplies the
   * `?`/`&` separator).
   */
  toQueryFragment: (pageParam: PageParam) => string;
}

/** Page-based adapter: `?page=N`, advance while `hasMore`. */
export const pageAdapter = <T,>(): PaginationAdapter<PageEnvelope<T>, T, number> => ({
  initialPageParam: 1,
  getItems: (page) => page.data ?? [],
  getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  toQueryFragment: (pageParam) => `page=${pageParam}`,
});

/** Offset-based adapter: `?offset=N&limit=M`, advance until offset ≥ totalCount. */
export const offsetAdapter = <T,>(limit = 50): PaginationAdapter<OffsetEnvelope<T>, T, number> => ({
  initialPageParam: 0,
  getItems: (page) => page.items ?? [],
  getNextPageParam: (lastPage, lastOffset) => {
    const next = lastOffset + (lastPage.limit || limit);
    return next < lastPage.totalCount ? next : undefined;
  },
  toQueryFragment: (offset) => `offset=${offset}&limit=${limit}`,
});

/** Canonical `$skip`/`$top` adapter for `ListEnvelope` endpoints (C-1). */
export const listEnvelopeAdapter = <T,>(top = 50): PaginationAdapter<ListEnvelope<T>, T, number> => ({
  initialPageParam: 0,
  getItems: (page) => page.items ?? [],
  getNextPageParam: (lastPage, lastSkip) => {
    const next = lastSkip + (lastPage.top || top);
    return next < lastPage.totalCount ? next : undefined;
  },
  // Bare names — the server's ListPaging.Parse accepts `$`-prefixed and bare
  // top/skip alike, and bare names need no URL-encoding of `$`.
  toQueryFragment: (skip) => `skip=${skip}&top=${top}`,
});

/** Cursor-based adapter: `?cursor=TOKEN`, advance while a cursor is returned. */
export const cursorAdapter = <T,>(): PaginationAdapter<CursorEnvelope<T>, T, string | undefined> => ({
  initialPageParam: undefined,
  getItems: (page) => page.results ?? [],
  getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  toQueryFragment: (cursor) => (cursor ? `cursor=${encodeURIComponent(cursor)}` : ''),
});

/** Append a query fragment to an endpoint, choosing `?` vs `&` correctly. */
export const appendQuery = (endpoint: string, fragment: string): string => {
  if (!fragment) return endpoint;
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}${fragment}`;
};
